import { useState, useRef, useCallback, useEffect } from 'react';
import { calculatePinchViewport, fitBoundsToViewport } from '../../utils/mindmap/viewport';

const VIEWPORT_STORAGE_PREFIX = 'mindMapViewport:';
const MIN_SCALE = 0.2;
const MAX_SCALE = 3;

const clampScaleValue = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return Math.min(Math.max(value, MIN_SCALE), MAX_SCALE);
};

const normalizeOffset = (candidate) => {
  if (!candidate || typeof candidate !== 'object') return null;
  const { x, y } = candidate;
  if (typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)) {
    return { x, y };
  }
  return null;
};

const readViewportFromStorage = (storageKey) => {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const storedScale = clampScaleValue(parsed.scale);
    const storedOffset = normalizeOffset(parsed.offset);
    if (!storedScale && !storedOffset) return null;
    return {
      scale: storedScale ?? 1,
      offset: storedOffset ?? { x: 0, y: 0 },
    };
  } catch (err) {
    console.error('Failed to read stored viewport:', err);
    return null;
  }
};

const readLegacyViewport = () => {
  if (typeof window === 'undefined') return null;
  try {
    const rawScale = window.sessionStorage.getItem('mindMapScale');
    const rawOffset = window.sessionStorage.getItem('mindMapOffset');
    const storedScale = rawScale ? clampScaleValue(parseFloat(rawScale)) : null;
    const storedOffset = rawOffset ? normalizeOffset(JSON.parse(rawOffset)) : null;
    if (!storedScale && !storedOffset) return null;
    return {
      scale: storedScale ?? 1,
      offset: storedOffset ?? { x: 0, y: 0 },
    };
  } catch (err) {
    console.error('Failed to read legacy viewport:', err);
    return null;
  }
};

const persistViewport = (key, viewport) => {
  if (typeof window === 'undefined') return;
  try {
    if (key) {
      window.localStorage.setItem(key, JSON.stringify(viewport));
    } else {
      window.sessionStorage.setItem('mindMapScale', String(viewport.scale));
      window.sessionStorage.setItem('mindMapOffset', JSON.stringify(viewport.offset));
    }
  } catch (err) {
    console.error('Failed to persist map viewport:', err);
  }
};

/**
 * Custom hook for handling map navigation: panning, zooming, and centering.
 * The caller supplies a generic moveNode function so that navigation can
 * work with either local state or a collaborative backend (e.g. Yjs).
 */
export const useMapNavigation = ({
  containerRef,
  root,
  nodes,
  moveNode,
  contentBounds = null,
  viewportStorageKey = null,
}) => {
  const resolvedStorageKey = viewportStorageKey
    ? `${VIEWPORT_STORAGE_PREFIX}${viewportStorageKey}`
    : null;

  const initialViewport = (() => {
    const stored = readViewportFromStorage(resolvedStorageKey);
    if (stored) return stored;
    return readLegacyViewport();
  })();

  const [scale, setScale] = useState(() => initialViewport?.scale ?? 1);
  const [offset, setOffset] = useState(() => initialViewport?.offset ?? { x: 0, y: 0 });
  const [draggingMap, setDraggingMap] = useState(false);
  const [didRestoreViewport, setDidRestoreViewport] = useState(Boolean(initialViewport));

  const storageKeyRef = useRef(resolvedStorageKey);
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);

  const dragStart = useRef(null);
  const activePointers = useRef(new Map());
  const pointerGesture = useRef(null);
  const suppressMouseUntil = useRef(0);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);


  const clampOffset = useCallback((nextOffset, scaleOverride = scale) => {
    if (!contentBounds) return nextOffset;
    const container = containerRef.current;
    if (!container) return nextOffset;

    const rect = container.getBoundingClientRect();
    const viewportWidth = rect.width;
    const viewportHeight = rect.height;
    const safeScale = Number.isFinite(scaleOverride) ? scaleOverride : 1;
    const { minX, maxX, minY, maxY } = contentBounds;

    const logicalPadX = viewportWidth / safeScale / 2;
    const logicalPadY = viewportHeight / safeScale / 2;

    const paddedMinX = minX - logicalPadX;
    const paddedMaxX = maxX + logicalPadX;
    const paddedMinY = minY - logicalPadY;
    const paddedMaxY = maxY + logicalPadY;

    let minOffsetX = viewportWidth - paddedMaxX * safeScale;
    let maxOffsetX = -paddedMinX * safeScale;
    let minOffsetY = viewportHeight - paddedMaxY * safeScale;
    let maxOffsetY = -paddedMinY * safeScale;

    if (minOffsetX > maxOffsetX) {
      const centeredX = (minOffsetX + maxOffsetX) / 2;
      minOffsetX = centeredX;
      maxOffsetX = centeredX;
    }

    if (minOffsetY > maxOffsetY) {
      const centeredY = (minOffsetY + maxOffsetY) / 2;
      minOffsetY = centeredY;
      maxOffsetY = centeredY;
    }

    const clampedX = Math.min(Math.max(nextOffset.x, minOffsetX), maxOffsetX);
    const clampedY = Math.min(Math.max(nextOffset.y, minOffsetY), maxOffsetY);

    return { x: clampedX, y: clampedY };
  }, [contentBounds, containerRef, scale]);

  const setOffsetClamped = useCallback((next) => {
    setOffset((prev) => {
      const target = typeof next === 'function' ? next(prev) : next;
      if (!target || typeof target.x !== 'number' || typeof target.y !== 'number') {
        return prev;
      }
      const clamped = clampOffset(target);
      if (clamped.x === prev.x && clamped.y === prev.y) {
        return prev;
      }
      return clamped;
    });
  }, [clampOffset]);

  useEffect(() => {
    if (storageKeyRef.current === resolvedStorageKey) return;
    storageKeyRef.current = resolvedStorageKey;

    const fallbackViewport = { scale: 1, offset: { x: 0, y: 0 } };

    if (!resolvedStorageKey) {
      const legacyViewport = readLegacyViewport();
      const nextViewport = legacyViewport || fallbackViewport;
      setScale(nextViewport.scale);
      setOffset(nextViewport.offset);
      setDidRestoreViewport(Boolean(legacyViewport));
      return;
    }

    const storedViewport = readViewportFromStorage(resolvedStorageKey);
    if (storedViewport) {
      setScale(storedViewport.scale);
      setOffset(storedViewport.offset);
      setDidRestoreViewport(true);
    } else {
      setScale(fallbackViewport.scale);
      setOffset(fallbackViewport.offset);
      setDidRestoreViewport(false);
    }
  }, [resolvedStorageKey]);

  /*
  const getRootNode = useCallback(() => {
    return nodes.find(n => n.id === root);
  }, [nodes, root]);
  */

  /*
  const moveRoot = useCallback((deltaX, deltaY, animate = false) => {
    const rootNode = getRootNode();
    if (!rootNode) return;

    const newPosition = {
      x: rootNode.position.x + deltaX,
      y: rootNode.position.y + deltaY
    };
    moveNode(root, newPosition, { animate });
  }, [getRootNode, moveNode, root]);
  */

  /*
  const animatedMoveRoot = useCallback((deltaX, deltaY, duration = 300) => {
    const start = performance.now();
    const rootNode = getRootNode();
    if (!rootNode) return;

    const startX = rootNode.position.x;
    const startY = rootNode.position.y;

    const easeOutQuad = (t) => t * (2 - t);

    const animate = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutQuad(t);

      const newX = startX + deltaX * eased;
      const newY = startY + deltaY * eased;
      moveNode(root, { x: newX, y: newY }, { animate: false });

      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [getRootNode, moveNode, root]);
  */

  const zoomAtCenter = useCallback((newScale) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Keep the logical point under the viewport center fixed across zoom
    const logicalCenterX = (centerX - offset.x) / scale;
    const logicalCenterY = (centerY - offset.y) / scale;

    const newOffsetX = centerX - logicalCenterX * newScale;
    const newOffsetY = centerY - logicalCenterY * newScale;

    setOffsetClamped({ x: newOffsetX, y: newOffsetY });
    setScale(newScale);
  }, [containerRef, offset, scale, setOffsetClamped]);

  /*
  const centerNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!node || !containerRect) return;

    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    const deltaX = centerX - node.position.x;
    const deltaY = centerY - node.position.y;

    animatedMoveRoot(deltaX, deltaY, 800);
  }, [nodes, containerRef, animatedMoveRoot]);
  */

  const centerNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!node || !containerRect) return;

    const viewportCenterX = containerRect.width / 2;
    const viewportCenterY = containerRect.height / 2;

    const newOffsetX = viewportCenterX - node.position.x * scale;
    const newOffsetY = viewportCenterY - node.position.y * scale;

    setOffsetClamped({ x: newOffsetX, y: newOffsetY });
  }, [nodes, containerRef, scale, setOffsetClamped]);

  const focusNodeIfOutOfView = useCallback((nodeId, buffer = 150) => {
    const node = nodes.find(n => n.id === nodeId);
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!node || !containerRect) return;

    const centerX = containerRect.width / 2 - 100;
    const centerY = containerRect.height / 2;

    const nodeScreenX = offset.x + node.position.x * scale;
    const nodeScreenY = offset.y + node.position.y * scale;

    const deltaX = nodeScreenX - centerX;
    const deltaY = nodeScreenY - centerY;

    if (Math.abs(deltaX) > buffer || Math.abs(deltaY) > buffer) {
      // Smoothly move the offset so the node comes back into view
      const start = { x: offset.x, y: offset.y };
      const target = { x: offset.x - deltaX, y: offset.y - deltaY };
      const startTime = performance.now();
      const duration = 300;

      const easeOutQuad = (t) => t * (2 - t);

      const animate = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        const eased = easeOutQuad(t);

        const newX = start.x + (target.x - start.x) * eased;
        const newY = start.y + (target.y - start.y) * eased;

        setOffsetClamped({ x: newX, y: newY });

        if (t < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [nodes, containerRef, offset, scale, setOffsetClamped]);

  // Mouse handlers for dragging
  const handleMouseDown = useCallback((e) => {
    if (Date.now() < suppressMouseUntil.current) return;
    const clickedEl = e.target;
    if (clickedEl.closest('.node')) return;

    dragStart.current = { x: e.clientX, y: e.clientY };
    setDraggingMap(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!draggingMap || !dragStart.current) return;

    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    // Update viewport offset instead of mutating node positions
    setOffsetClamped(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    dragStart.current = { x: e.clientX, y: e.clientY };
  }, [draggingMap, setOffsetClamped]);

  const handleMouseUp = useCallback(() => {
    setDraggingMap(false);
    dragStart.current = null;
  }, []);

  const canStartCanvasGesture = useCallback((target) => {
    if (!target?.closest) return true;
    return !target.closest('.node, button, input, textarea, [contenteditable="true"], [data-export-ignore="true"]');
  }, []);

  const getCanvasPointerPoint = useCallback((event) => {
    const rect = containerRef.current?.getBoundingClientRect?.();
    return {
      x: event.clientX - (rect?.left || 0),
      y: event.clientY - (rect?.top || 0),
    };
  }, [containerRef]);

  const startPinchGesture = useCallback(() => {
    const points = [...activePointers.current.values()].slice(0, 2);
    if (points.length !== 2) return;
    pointerGesture.current = {
      mode: 'pinch',
      initialPoints: points.map((point) => ({ x: point.x, y: point.y })),
      initialViewport: {
        scale: scaleRef.current,
        offset: { ...offsetRef.current },
      },
    };
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse') {
      return { tracked: false, startedMultiTouch: false };
    }
    if (activePointers.current.size >= 2) {
      // Ignore additional fingers without allowing a node/control beneath the
      // third touch to begin a competing gesture.
      event.preventDefault();
      return { tracked: false, startedMultiTouch: false };
    }
    suppressMouseUntil.current = Date.now() + 700;
    const startsOnCanvas = canStartCanvasGesture(event.target);
    const point = { ...getCanvasPointerPoint(event), startsOnCanvas };
    activePointers.current.set(event.pointerId, point);
    if (activePointers.current.size === 1) {
      pointerGesture.current = {
        mode: startsOnCanvas ? 'pan' : 'blocked',
        lastPoint: point,
      };
      if (startsOnCanvas) {
        event.currentTarget?.setPointerCapture?.(event.pointerId);
        setDraggingMap(true);
      }
      return { tracked: true, startedMultiTouch: false };
    }

    // A second touch always transfers ownership to canvas navigation, even
    // when either touch began over a node or control. The capture-phase
    // caller uses this signal to cancel the node's pending gesture.
    event.preventDefault();
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    startPinchGesture();
    setDraggingMap(true);
    return { tracked: true, startedMultiTouch: true };
  }, [canStartCanvasGesture, getCanvasPointerPoint, startPinchGesture]);

  const handlePointerMove = useCallback((event) => {
    if (event.pointerType === 'mouse' || !activePointers.current.has(event.pointerId)) return;
    event.preventDefault();
    const point = getCanvasPointerPoint(event);
    activePointers.current.set(event.pointerId, point);

    const gesture = pointerGesture.current;
    if (activePointers.current.size >= 2 && gesture?.mode === 'pinch') {
      const currentPoints = [...activePointers.current.values()]
        .slice(0, 2)
        .map((currentPoint) => ({ x: currentPoint.x, y: currentPoint.y }));
      const nextViewport = calculatePinchViewport({
        initialViewport: gesture.initialViewport,
        initialPoints: gesture.initialPoints,
        currentPoints,
        minScale: MIN_SCALE,
        maxScale: MAX_SCALE,
      });
      const nextOffset = clampOffset(nextViewport.offset, nextViewport.scale);
      scaleRef.current = nextViewport.scale;
      offsetRef.current = nextOffset;
      setScale(nextViewport.scale);
      setOffset(nextOffset);
      return;
    }

    if (activePointers.current.size === 1 && gesture?.mode === 'pan') {
      const delta = {
        x: point.x - gesture.lastPoint.x,
        y: point.y - gesture.lastPoint.y,
      };
      const nextOffset = clampOffset({
        x: offsetRef.current.x + delta.x,
        y: offsetRef.current.y + delta.y,
      });
      offsetRef.current = nextOffset;
      setOffset(nextOffset);
      pointerGesture.current = {
        mode: 'pan',
        lastPoint: point,
      };
    }
  }, [clampOffset, getCanvasPointerPoint]);

  const endPointerGesture = useCallback((event) => {
    if (event.pointerType === 'mouse' || !activePointers.current.has(event.pointerId)) {
      return { ended: false, finished: activePointers.current.size === 0 };
    }
    const wasPinching = pointerGesture.current?.mode === 'pinch';
    activePointers.current.delete(event.pointerId);
    try {
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
    } catch {
      // The first pointer may still be captured by the node it began on.
    }
    const remainingPoint = [...activePointers.current.values()][0];
    if (remainingPoint) {
      // A pinch owns the complete multi-touch sequence. Requiring all fingers
      // to lift avoids an accidental pan or node drag when one finger remains.
      pointerGesture.current = {
        mode: wasPinching ? 'blocked' : (remainingPoint.startsOnCanvas ? 'pan' : 'blocked'),
        lastPoint: { ...remainingPoint },
      };
      setDraggingMap(!wasPinching && remainingPoint.startsOnCanvas);
    } else {
      pointerGesture.current = null;
      setDraggingMap(false);
    }
    return { ended: true, finished: activePointers.current.size === 0 };
  }, []);

  // Persist the current viewport so zoom/pan restore on refresh per map
  useEffect(() => {
    persistViewport(resolvedStorageKey, { scale, offset });
  }, [scale, offset, resolvedStorageKey]);


  useEffect(() => {
    setOffsetClamped((prev) => prev);
  }, [contentBounds, scale, setOffsetClamped]);

  // Wheel handler for zooming
  const handleWheel = useCallback((e) => {
    const isZoomGesture = e.ctrlKey || Math.abs(e.deltaZ) > 0;
    if (!isZoomGesture) return;

    e.preventDefault();

    const zoomIntensity = 0.0015;
    const delta = e.deltaY || e.deltaZ;
    let newScale = scale * (1 - delta * zoomIntensity);
    newScale = Math.max(0.2, Math.min(3, newScale));

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const centerX = e.clientX - rect.left;
    const centerY = e.clientY - rect.top;

    const logicalX = (centerX - offset.x) / scale;
    const logicalY = (centerY - offset.y) / scale;

    const newOffsetX = centerX - logicalX * newScale;
    const newOffsetY = centerY - logicalY * newScale;

    setOffsetClamped({ x: newOffsetX, y: newOffsetY });
    setScale(newScale);
  }, [scale, containerRef, offset, setOffsetClamped]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    const newScale = Math.min(scale + 0.1, 2);
    zoomAtCenter(newScale);
  }, [scale, zoomAtCenter]);

  const zoomOut = useCallback(() => {
    const newScale = Math.max(scale - 0.1, 0.3);
    zoomAtCenter(newScale);
  }, [scale, zoomAtCenter]);

  const resetZoom = useCallback(() => {
    zoomAtCenter(1);
  }, [zoomAtCenter]);

  const fitToBounds = useCallback((bounds, options = {}) => {
    const container = containerRef.current;
    if (!container || !bounds) return null;
    const rect = container.getBoundingClientRect();
    const viewport = fitBoundsToViewport(bounds, {
      width: rect.width,
      height: rect.height,
    }, options);
    setScale(viewport.scale);
    setOffset(viewport.offset);
    return viewport;
  }, [containerRef]);

  // Set up wheel listener
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [handleWheel, containerRef]);

  return {
    // State
    scale,
    offset,
    draggingMap,
    didRestoreViewport,

    // Navigation functions
    centerNode,
    focusNodeIfOutOfView,
    zoomAtCenter,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToBounds,

    // Event handlers
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp: endPointerGesture,
    handlePointerCancel: endPointerGesture,

    // Viewport helpers for features like undo/redo
    getViewport: () => ({ scale, offset }),
    setViewport: (viewport) => {
      if (!viewport) return;

      const currentViewport = {
        scale: scaleRef.current,
        offset: offsetRef.current,
      };

      const nextViewport =
        typeof viewport === 'function'
          ? viewport(currentViewport)
          : viewport;

      if (!nextViewport || typeof nextViewport !== 'object') return;

      if (typeof nextViewport.scale === 'number' && Number.isFinite(nextViewport.scale)) {
        setScale(nextViewport.scale);
      }

      if (
        nextViewport.offset &&
        typeof nextViewport.offset.x === 'number' &&
        typeof nextViewport.offset.y === 'number' &&
        Number.isFinite(nextViewport.offset.x) &&
        Number.isFinite(nextViewport.offset.y)
      ) {
        setOffsetClamped({ x: nextViewport.offset.x, y: nextViewport.offset.y });
      }
    }
  };
};
